import { useState, useEffect, useCallback } from 'react';
import { useCampus } from '../lib/useCampus';
import { api, ApiError, asList } from '../lib/api';
import { housesApi, leadershipApi, type House, type HouseBoard, type Post, type Appointment, type Election } from '../lib/leadershipApi';
import { HouseFlags } from '../components/HouseFlags';
import { HouseAllocationPanel } from '../components/HouseAllocationPanel';

function currentAcademicYear() { const n = new Date(); return n.getMonth() >= 5 ? `${n.getFullYear()}-${n.getFullYear() + 1}` : `${n.getFullYear() - 1}-${n.getFullYear()}`; }

export function HousesLeadershipPage() {
  const [tab, setTab] = useState<'houses' | 'posts' | 'elections'>('houses');
  const [instId, setInstId] = useState<string | null>(null);
  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Student life</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Houses & leadership</h1>
      </div>
      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        <button className={tab === 'houses' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('houses')}>Houses</button>
        <button className={tab === 'posts' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('posts')}>Posts & appointments</button>
        <button className={tab === 'elections' ? 'btn btn-accent' : 'btn'} onClick={() => setTab('elections')}>Elections</button>
      </div>
      {tab === 'houses' && <HousesTab instId={instId} />}
      {tab === 'posts' && <PostsTab instId={instId} />}
      {tab === 'elections' && <ElectionsTab />}
    </div>
  );
}

function HousesTab({ instId }: { instId: string | null }) {
  const [board, setBoard] = useState<HouseBoard | null>(null);
  const [year, setYear] = useState<string | undefined>(undefined);
  const [name, setName] = useState(''); const [color, setColor] = useState('#c1272d'); const [motto, setMotto] = useState('');
  const load = useCallback(() => {
    housesApi.leaderboard(year).then(setBoard).catch(() => setBoard(null));
  }, [year]);
  useEffect(load, [load]);

  const houses = board?.houses ?? [];
  /**
   * ⚠ Past seasons are READ-ONLY. Awarding a point to a competition that finished in April is how
   * a settled result quietly changes months later, and the only person who would notice is the
   * house that was beaten.
   */
  const isPast = !!board && !board.isAllTime && board.academicYear !== currentAcademicYear();
  const locked = board?.isAllTime || isPast;

  const add = async () => { if (!name.trim() || !instId) return; await housesApi.create({ name: name.trim(), institutionId: instId, colorHex: color, motto: motto.trim() || null }); setName(''); setMotto(''); load(); };
  const award = async (h: House) => {
    const pts = Number(window.prompt(`Award points to ${h.name} (use negative to deduct):`));
    if (!pts) return;
    const reason = window.prompt('Reason?') || 'Manual award';
    await housesApi.award({ houseId: h.id, points: pts, source: 'Manual', reason, studentId: null }); load();
  };
  const retire = async (h: House) => {
    if (!window.confirm(`Take ${h.name} off the board?\n\nIts points, its posts and every past appointment stay exactly as they are — the house simply stops competing.`)) return;
    await housesApi.retire(h.id); load();
  };

  return (
    <div>
      {/* ⚠ THE FLAGS ARE THE SCOREBOARD, DRAWN — ordered by points, leader highest. */}
      {board && (
        <HouseFlags
          houses={houses.filter((h) => h.isActive)}
          leaderIds={board.leaderIds}
          isTie={board.isTie}
          season={board.isAllTime ? 'All time' : board.academicYear}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 className="h-display" style={{ fontSize: 18, margin: 0 }}>Leaderboard</h2>
        {/* ⚠ The season picker lists only sessions that HAVE points. A school that started in 2026
            has no 2024 season, and offering one invites somebody to select it and conclude their
            data has been lost. */}
        <select value={year ?? currentAcademicYear()} onChange={(e) => setYear(e.target.value)}>
          {[...new Set([currentAcademicYear(), ...(board?.seasons ?? [])])]
            .sort().reverse()
            .map((s) => <option key={s} value={s}>{s}</option>)}
          <option value="all">All time (honours board)</option>
        </select>
        {locked && (
          <span className="pill" style={{ fontSize: 12 }}>
            {board?.isAllTime ? 'Every season added together' : 'A finished season — read only'}
          </span>
        )}
      </div>

      {/* ⚠ Only on the live season. Allocating children into a competition that finished in April
          is not a thing anybody means to do. */}
      {!locked && houses.length > 0 && <HouseAllocationPanel institutionId={instId} onDone={load} />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
        {houses.map((h, i) => (
          <div key={h.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, borderLeft: `5px solid ${h.colorHex}`, opacity: h.isActive ? 1 : 0.55 }}>
            {/*
              ⚠ POSITION, NOT ROW NUMBER — 1, 1, 3, 4 and not 1, 2, 3, 4.
              The flags above already draw a tie as a tie, because the server says who is leading.
              Printing i+1 underneath them put Aravalli first and Nilgiri second on identical points,
              which is the same mistake in the same screen, contradicting the picture beside it. Two
              houses level on 287 are joint first and the row after them is third.
            */}
            <span style={{ fontWeight: 700, fontSize: 18, width: 28 }}
              title={houses.findIndex((x) => x.points === h.points) !== i ? 'Joint position' : undefined}>
              {houses.findIndex((x) => x.points === h.points) + 1}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>
                {h.name}{!h.isActive && <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 400 }}> · retired</span>}
              </div>
              {h.motto && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{h.motto}</div>}
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{h.members} members</div>
            </div>
            <span style={{ fontWeight: 800, fontSize: 22 }}>{h.points}</span>
            {!locked && h.isActive && <button className="btn" onClick={() => award(h)}>Award</button>}
            {!locked && h.isActive && <button className="btn" onClick={() => retire(h)}>Retire</button>}
          </div>
        ))}
        {houses.length === 0 && <div style={{ color: 'var(--ink-3)', padding: 12 }}>No houses yet.</div>}
      </div>

      <div className="card" style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" placeholder="House name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: '1 1 160px' }} />
        <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 44, height: 36, border: 'none', background: 'none' }} />
        <input className="input" placeholder="Motto (optional)" value={motto} onChange={(e) => setMotto(e.target.value)} style={{ flex: '1 1 160px' }} />
        <button className="btn btn-accent" disabled={!instId} onClick={add}>Add house</button>
      </div>
    </div>
  );
}

function PostsTab({ instId }: { instId: string | null }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [title, setTitle] = useState(''); const [category, setCategory] = useState(''); const [rank, setRank] = useState('1');

  const load = useCallback(() => {
    leadershipApi.posts().then((r) => setPosts(asList(r))).catch(() => setPosts([]));
    leadershipApi.appointments().then((r) => setAppts(asList(r))).catch(() => setAppts([]));
  }, []);
  useEffect(load, [load]);

  const addPost = async () => { if (!title.trim() || !instId) return; await leadershipApi.createPost({ title: title.trim(), institutionId: instId, category: category.trim() || null, houseId: null, rank: Number(rank) || 1 }); setTitle(''); setCategory(''); load(); };

  return (
    <div>
      <h2 className="h-display" style={{ fontSize: 18, marginBottom: 10 }}>Current post-holders</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
        {appts.length === 0 ? <div style={{ color: 'var(--ink-3)', padding: 12 }}>No appointments yet.</div>
          : appts.map((a) => (
            <div key={a.id} className="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{a.postTitle}</span>
                <span style={{ color: 'var(--ink-2)' }}> — {a.studentName}</span>
                {a.viaElection && <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Elected</span>}
              </div>
              <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{a.academicYear}</span>
              <button className="btn" onClick={async () => { if (window.confirm('End this appointment?')) { await leadershipApi.endAppointment(a.id); load(); } }}>End</button>
            </div>
          ))}
      </div>

      <h2 className="h-display" style={{ fontSize: 18, marginBottom: 10 }}>Posts</h2>
      <div className="card" style={{ padding: 14, marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input" placeholder="Post (e.g. School President)" value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: '2 1 180px' }} />
        <input className="input" placeholder="Category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: 130 }} />
        <input className="input" type="number" placeholder="Rank" value={rank} onChange={(e) => setRank(e.target.value)} style={{ width: 70 }} />
        <button className="btn btn-accent" disabled={!instId} onClick={addPost}>Add post</button>
      </div>
      {posts.map((p) => <AppointRow key={p.id} post={p} onAppointed={load} />)}
    </div>
  );
}

function AppointRow({ post, onAppointed }: { post: Post; onAppointed: () => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(''); const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);
  const doSearch = async () => { if (search.trim().length < 2) return; try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=6`))); } catch { setResults([]); } };
  const appoint = async (id: string, nm: string) => {
    await leadershipApi.appoint({ postId: post.id, studentId: id, studentName: nm, houseId: post.houseId, academicYear: currentAcademicYear(), startDate: new Date().toISOString().slice(0, 10), endDate: null });
    setOpen(false); setResults([]); setSearch(''); onAppointed();
  };
  return (
    <div className="card" style={{ padding: 12, marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}><span style={{ fontWeight: 600 }}>{post.title}</span>{post.category && <span style={{ fontSize: 12, color: 'var(--ink-3)' }}> · {post.category}</span>}</div>
        <button className="btn" onClick={() => setOpen(!open)}>Appoint</button>
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input className="input" placeholder="Find student" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
            <button className="btn" onClick={doSearch}>Search</button>
          </div>
          {results.map((s) => <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }} onClick={() => appoint(s.id, s.fullName)}>{s.fullName} · {s.admissionNumber}</button>)}
        </div>
      )}
    </div>
  );
}

function ElectionsTab() {
  const [elections, setElections] = useState<Election[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [postId, setPostId] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => { leadershipApi.elections().then((r) => setElections(asList(r))).catch(() => setElections([])); }, []);
  useEffect(load, [load]);
  useEffect(() => { leadershipApi.posts().then((r) => setPosts(asList(r))).catch(() => setPosts([])); }, []);

  const create = async () => { if (!postId) return; await leadershipApi.createElection({ postId, academicYear: currentAcademicYear(), electorate: 'WholeSchool', electorateRefId: null }); load(); };

  const act = async (fn: () => Promise<unknown>) => { setMsg(null); try { await fn(); load(); } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Action failed.'); } };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input" value={postId} onChange={(e) => setPostId(e.target.value)}>
          <option value="">Post to elect…</option>
          {posts.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <button className="btn btn-accent" onClick={create}>Start election</button>
        {msg && <span style={{ fontSize: 13, color: '#c1272d' }}>{msg}</span>}
      </div>

      {elections.map((e) => <ElectionCard key={e.id} election={e} onAct={act} />)}
      {elections.length === 0 && <div style={{ color: 'var(--ink-3)', padding: 12 }}>No elections yet.</div>}
    </div>
  );
}

function ElectionCard({ election: e, onAct }: { election: Election; onAct: (fn: () => Promise<unknown>) => void }) {
  const [search, setSearch] = useState(''); const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);
  const doSearch = async () => { if (search.trim().length < 2) return; try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=6`))); } catch { setResults([]); } };

  const showTally = e.status === 'Closed' || e.status === 'Declared';
  const pill = { Draft: '#888', Voting: '#1a7f37', Closed: '#b26a00', Declared: '#2456b2' }[e.status] ?? '#888';

  return (
    <div className="card" style={{ padding: 16, marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontWeight: 600, flex: 1 }}>{e.postTitle}</span>
        <span className="pill" style={{ fontSize: 11, background: pill, color: '#fff' }}>{e.status}</span>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{e.totalVotes} votes</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
        {e.candidates.map((c) => (
          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <span style={{ flex: 1 }}>{c.studentName}{e.winnerCandidateId === c.id && <strong style={{ color: '#1a7f37' }}> · Winner</strong>}</span>
            {showTally && <span style={{ fontWeight: 600 }}>{c.votes}</span>}
          </div>
        ))}
        {e.candidates.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>No candidates yet.</span>}
      </div>

      {e.status === 'Draft' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <input className="input" placeholder="Add candidate" value={search} onChange={(ev) => setSearch(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && doSearch()} />
            <button className="btn" onClick={doSearch}>Find</button>
          </div>
          {results.map((s) => <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }}
            onClick={() => onAct(async () => { await leadershipApi.addCandidate({ electionId: e.id, studentId: s.id, studentName: s.fullName, manifesto: null }); setResults([]); setSearch(''); })}>{s.fullName} · {s.admissionNumber}</button>)}
          <button className="btn btn-accent" style={{ marginTop: 6 }} onClick={() => onAct(() => leadershipApi.open(e.id))}>Open voting</button>
        </>
      )}
      {e.status === 'Voting' && <button className="btn" onClick={() => onAct(() => leadershipApi.close(e.id))}>Close voting</button>}
      {e.status === 'Closed' && <button className="btn btn-accent" onClick={() => onAct(() => leadershipApi.declare(e.id, new Date().toISOString().slice(0, 10), null))}>Declare winner & appoint</button>}
    </div>
  );
}
